import { Maximize2, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'

import type {
    CareLogCardArea,
    CareLogCardMaterial,
} from './-care-log-list-card'
import type { MediaItem } from '@/components/common/media-lightbox'
import {
    MediaLightbox,
    MediaThumbStrip,
} from '@/components/common/media-lightbox'
import { AreaPolygonThumb } from '@/components/growers/area-map/area-polygon-thumb'
import { Button } from '@/components/ui/button'
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet'
import { agriUnitLabel } from '@/lib/agri-unit-label'
import { m } from '@/lib/paraglide/messages'

export interface CareLogDetail {
    id: string
    grower: string
    activity: string
    logDate: string
    implementers: string
    areas: CareLogCardArea[]
    materials: CareLogCardMaterial[]
    amountLabel?: string
    note?: string
    quarantineNote?: string
    files?: MediaItem[]
}

interface Props {
    log: CareLogDetail | null
    onOpenChange: (open: boolean) => void
    canWrite: boolean
    canDelete: boolean
    onEdit: (id: string) => void
    onDelete: (id: string) => void
    onOpenMap: () => void
}

function Field({
    label,
    children,
}: {
    label: string
    children: React.ReactNode
}) {
    return (
        <div className="space-y-1.5">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {label}
            </div>
            {children}
        </div>
    )
}

/**
 * Chi tiết một nhật ký chăm sóc, mở từ card danh sách. Sửa/Xoá nằm ở chân màn
 * chi tiết chứ không phải icon nhỏ trên card: trên điện thoại hai icon cạnh
 * nhau rất dễ bấm nhầm Xoá, và hộ dân muốn xem lại đã bón gì trước khi quyết
 * định sửa hay xoá.
 */
export function CareLogDetailSheet({
    log,
    onOpenChange,
    canWrite,
    canDelete,
    onEdit,
    onDelete,
    onOpenMap,
}: Props) {
    const firstArea = log?.areas[0]
    // Vị trí tệp đang xem PHẢI gắn với id nhật ký: sheet chỉ mount một lần ở
    // route rồi đổi prop `log`, nên một vị trí trần sẽ sống sót qua lần đổi
    // nhật ký. Danh sách refetch làm dòng đang mở biến mất thì sheet đóng mà
    // không đi qua nút Đóng, và nhật ký mở sau đó sẽ tự bung popup ở đúng vị
    // trí cũ. So id ở đây khiến trạng thái treo đó không thể xảy ra.
    const [media, setMedia] = useState<{ logId: string; index: number } | null>(
        null,
    )
    const mediaIndex = media && media.logId === log?.id ? media.index : null
    const openMedia = (index: number) =>
        log && setMedia({ logId: log.id, index })
    const files = log?.files ?? []
    return (
        <Sheet open={!!log} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                className="max-h-[90svh] overflow-y-auto p-0"
            >
                {log && (
                    <>
                        <SheetHeader className="border-b px-4 pb-3">
                            <SheetTitle className="text-base text-primary">
                                {log.grower}
                            </SheetTitle>
                            <div>
                                <span className="inline-flex rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-accent-foreground">
                                    {log.activity}
                                </span>
                            </div>
                        </SheetHeader>

                        <div className="space-y-4 px-4 py-4">
                            <Field label={m.care_log_info_section()}>
                                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                                    <dt className="text-muted-foreground">
                                        {m.care_log_log_date()}
                                    </dt>
                                    <dd className="tabular-nums">
                                        {log.logDate}
                                    </dd>
                                    <dt className="text-muted-foreground">
                                        {m.care_log_implementers()}
                                    </dt>
                                    <dd>{log.implementers}</dd>
                                </dl>
                            </Field>

                            <Field label={m.care_log_parcels()}>
                                {log.areas.length === 0 ? (
                                    <div className="text-sm text-muted-foreground">
                                        {m.care_log_area_none()}
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex flex-wrap gap-1.5">
                                            {log.areas.map((area) => (
                                                <span
                                                    key={area.id}
                                                    className="rounded-full bg-muted px-2 py-0.5 text-xs"
                                                >
                                                    {area.name}
                                                    {area.acreage ? (
                                                        <span className="ml-1 tabular-nums text-muted-foreground">
                                                            {m.care_log_area_acreage(
                                                                {
                                                                    acreage:
                                                                        area.acreage,
                                                                },
                                                            )}
                                                        </span>
                                                    ) : null}
                                                </span>
                                            ))}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={onOpenMap}
                                            aria-label={m.care_log_view_map()}
                                            className="relative block h-28 w-full cursor-zoom-in overflow-hidden rounded-md border"
                                        >
                                            <AreaPolygonThumb
                                                paths={firstArea?.paths}
                                                markers={firstArea?.markers}
                                                className="size-full"
                                            />
                                            <span className="absolute bottom-1 right-1 rounded bg-black/60 p-1 text-white">
                                                <Maximize2 className="size-3" />
                                            </span>
                                        </button>
                                    </>
                                )}
                            </Field>

                            {log.materials.length > 0 && (
                                <Field
                                    label={
                                        log.amountLabel ??
                                        m.care_log_amount_used()
                                    }
                                >
                                    <div className="space-y-1.5">
                                        {log.materials.map((mat, i) => (
                                            <div
                                                key={`${mat.name}-${i}`}
                                                className="flex items-baseline justify-between gap-3 rounded-md border px-3 py-2 text-sm"
                                            >
                                                <span className="min-w-0 break-words">
                                                    {mat.name}
                                                    {mat.selfBought && (
                                                        <span className="ml-1 text-xs text-muted-foreground">
                                                            {m.care_log_self_bought_tag()}
                                                        </span>
                                                    )}
                                                </span>
                                                <span className="shrink-0 font-semibold tabular-nums text-primary">
                                                    {mat.quantity}
                                                    {mat.unit
                                                        ? ` ${agriUnitLabel(mat.unit)}`
                                                        : ''}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </Field>
                            )}

                            {log.quarantineNote && (
                                <Field label={m.care_log_quarantine_note()}>
                                    <div className="text-sm">
                                        {log.quarantineNote}
                                    </div>
                                </Field>
                            )}

                            {log.note && (
                                <Field label={m.care_log_note()}>
                                    <div className="text-sm">{log.note}</div>
                                </Field>
                            )}

                            {files.length > 0 && (
                                <Field label={m.care_log_evidence()}>
                                    {/* Video phải ra thẻ <video>: trước đây mọi
                                        tệp đều render <img> nên video minh
                                        chứng chỉ hiện một ô vỡ. */}
                                    <MediaThumbStrip
                                        items={files}
                                        onOpen={openMedia}
                                        thumbClassName="size-16"
                                        className="gap-2"
                                    />
                                </Field>
                            )}
                        </div>

                        {(canWrite || canDelete) && (
                            <div className="sticky bottom-0 flex gap-2 border-t bg-background px-4 py-3">
                                {canWrite && (
                                    <Button
                                        className="flex-1"
                                        onClick={() => onEdit(log.id)}
                                    >
                                        <Pencil className="mr-1 size-4" />
                                        {m.care_log_edit()}
                                    </Button>
                                )}
                                {canDelete && (
                                    <Button
                                        variant="outline"
                                        className="flex-1 text-destructive"
                                        onClick={() => onDelete(log.id)}
                                    >
                                        <Trash2 className="mr-1 size-4" />
                                        {m.care_log_delete()}
                                    </Button>
                                )}
                            </div>
                        )}
                    </>
                )}
            </SheetContent>

            <MediaLightbox
                items={files}
                index={mediaIndex}
                onIndexChange={openMedia}
                onClose={() => setMedia(null)}
            />
        </Sheet>
    )
}
